import { HelpfulState } from '../kb/article-page/feedback/helpful-state.enum';
import {
	Article,
	SearchResult,
	ArticleSortType,
	SourceType,
	FileUploadResponse,
	User,
	RandomGenerator,
	HttpService,
} from '@wix/answers-api';
import axios, { AxiosPromise } from 'axios';
import { RawChatMessage, RawChatTimelineEvent, ChatTimelineMessage
	} from '../new-widget-container/chat-room/chat-timeline/types';
import { createDuplexerPubSub, createPresencePort, PresencePort } from '@wix/answers-app-core';
import { Duplexer } from '@wix/wix-duplexer-js';
import { SubscribeToUserTimeline } from '../widget-container';
import { noop, SmartPoller } from '@wix/answers-lib';
import { Logger } from '../../common/logger/logger';
import { PubSubEventType } from '@wix/answers-app-core/dist/ports/presence/duplexer-pubsub';
import { EventType } from '../../common/logger/event-type';
// tslint:disable-next-line:no-var-requires
const queryString = require('query-string');

export const AUTH_TOKEN_NAME = 'Authorization';
const PRESENCE_INTERVAL = 1000;
const DUPLEXER_API_KEY = '21d0f219-45e6-4df7-8d01-4a25b466efc1';
const AUTH_PATH = 'apps/widget/auth-endpoint';
const DUPLEXER_SERVER_URL = 'duplexer.wix.com';

export interface StartChatResponse {
	userId: string;
	ticketId: string;
}

export interface ChatParticipant {
	id: string;
	firstName: string;
	fullName: string;
	profileImage: string;
}

export interface ActiveAgent {
	firstName: string;
	profileImage: string;
}

export type ChatUserTimeline = {
	ticket: {
		creationDate: number;
		chatStatus: number;
	};
	timelineItems: Array<RawChatMessage | RawChatTimelineEvent>;
};

export type Attachment = {
	name: string;
	url: string;
	sizeBytes: number;
	contentType: string;
};

export type StartChatDto = {
	widgetId: string;
	locale: string;
	customFields: {[key: string]: string};
	userEmail: string;
	userFirstName: string;
};

export type ChatApi = {
	startChat: (dto: StartChatDto) => Promise<StartChatResponse>;
	updateEmail: (userId: string, email: string) => Promise<void>;
	getChatTimeline: (ticketId: string) => Promise<ChatUserTimeline>;
	getActiveChatAgents: () => Promise<User[]>;
	sendChatMessage: (ticketId: string, content: string) => AxiosPromise;
	sendChatFile: (ticketId: string, attachments: Attachment[]) => AxiosPromise;
	subscribeToUserTimeline: SubscribeToUserTimeline;
	presence: PresencePort;
};

export const createChatApi = (
	apiUrl: string, tenantName: string, tenantId: string,
	logger: Logger, getAuthToken: () => Promise<string | void>): ChatApi => {
	const CHAT_TOKEN_NAME = 'X-Answers-AGS';
	const POLLING_INTERVAL = 5000;
	const MAX_RETRIES = 5;
	const requestHeaders: {[key: string]: string} = {};
	const implicitEventLoggingFields: any = {};

	const updateAuthHeader = async () => {
		const token = await getAuthToken();
		if (token) {
			requestHeaders[AUTH_TOKEN_NAME] = token;
		}
	};

	const updateEmail = (userId: string, email: string): Promise<void> => {
		return new Promise((res, rej) =>
			axios.put(`${apiUrl}/chats/new/user`, { userId, email, firstName: '' }, {headers: requestHeaders})
				.then((response) => {
					const newToken = response.headers[CHAT_TOKEN_NAME.toLowerCase()];
					if (newToken) {
						requestHeaders[CHAT_TOKEN_NAME] = newToken;
					}
					res();
				})
				.catch((e) => rej(e))
		);
	};

	const getActiveChatAgents = (): Promise<User[]> => {
		return new Promise((res, rej) =>
			axios.get(`${apiUrl}/chats/active-agents`)
				.then((response) => {
					const data: User[] = response.data;
					const partialRandomAgents = data.map((activeAgent) => ({...activeAgent, id: RandomGenerator.id()}));
					res(partialRandomAgents);
				})
				.catch((e) => rej(e))
		);
	};

	const getChatTimeline = (ticketId: string): Promise<ChatUserTimeline> => {
		return new Promise(async (res, rej) => {
			await updateAuthHeader();
			axios.get(`${apiUrl}/tickets/${ticketId}/userTimeline`, {headers: requestHeaders})
				.then((response) => {
					const data = response.data;
					res(data);
				})
				.catch((e) => rej(e));
			});
	};

	const startChat = (dto: StartChatDto): Promise<{ticketId: string, userId: string}> => {

		return new Promise(async (res, rej) => {
			await updateAuthHeader();
			axios.post(`${apiUrl}/chats/new`, dto, {headers: requestHeaders})
				.then((response) => {
					requestHeaders[CHAT_TOKEN_NAME] = response.headers[CHAT_TOKEN_NAME.toLowerCase()];
					const data: {ticketId: string, userId: string} = response.data;
					implicitEventLoggingFields.ticketId = data.ticketId;
					implicitEventLoggingFields.userId = data.userId;
					res(data);
				})
				.catch((e) => {
					rej(e);
				});
			});
	};

	const sendChatMessage = async (ticketId: string, content: string) => {
		await updateAuthHeader();
		return axios.post(`${apiUrl}/chats/new/${ticketId}/userMessage`, { content }, {headers: requestHeaders});
	};

	const sendChatFile = async (ticketId: string, attachments: Attachment[]) => {
		await updateAuthHeader();
		return axios.post(`${apiUrl}/chats/new/${ticketId}/userMessage`, { attachments }, {headers: requestHeaders});
	};

	const apiKey = DUPLEXER_API_KEY;
	const hostUrl = apiUrl.replace('/api/v1', '');
	const duplexerUrl = DUPLEXER_SERVER_URL;

	const presencePubSub = createDuplexerPubSub(new Duplexer(duplexerUrl), apiKey, hostUrl, noop);

	const realTimeLogger = (type: PubSubEventType, data?: any) => {
		logger.logEvent(EventType.PUBSUB_EVENT, {...implicitEventLoggingFields, ...data, type});
	};
	const realTimePubSub = createDuplexerPubSub(new Duplexer(duplexerUrl), apiKey, hostUrl, realTimeLogger);
	const presence = createPresencePort(presencePubSub, {interval: PRESENCE_INTERVAL}, AUTH_PATH, tenantName);
	const subscribeToUserTimeline: SubscribeToUserTimeline = async (ticketId, handler, fallbackHandler) => {
		implicitEventLoggingFields.ticketId = ticketId;
		const channelName = `USER-${tenantId}-${ticketId}`;
		const messageEventName = 'agentChatMessageAdded';
		const historyEventName = 'ticketHistoryEvent';
		const authPath = `api/v1/chats/new/${ticketId}/realtime/authUser`;
		const safelyParse = (cb: (payload: ChatTimelineMessage) => void) => (str: string) => {
			try {
				cb(JSON.parse(str));
			} catch (error) {
				// tslint:disable-next-line:no-console
				console.error(`Couldn't parse rt update: ${error}`);
			}
		};

		const onFallback = () => fallbackHandler ? SmartPoller(
			() => getChatTimeline(ticketId),
			fallbackHandler,
			{document, interval: POLLING_INTERVAL, maxRetries: MAX_RETRIES, navigator}
		) : () => null;

		await updateAuthHeader();

		try {
			const unsubscribeMessage = await realTimePubSub.subscribe(channelName,
				messageEventName, safelyParse(handler), authPath, onFallback, false, {headers: requestHeaders});
			const unsubscribeHistory = await realTimePubSub.subscribe(channelName,
				historyEventName, safelyParse(handler), authPath, noop, false, {headers: requestHeaders});
			return () => {
				unsubscribeHistory();
				unsubscribeMessage();
			};
		} catch (error) {
			// tslint:disable-next-line:no-console
			console.log(`Could not subscribe to real time updates: ${error}`);
			throw error;
		}
	};

	return {
		startChat,
		updateEmail,
		getChatTimeline,
		getActiveChatAgents,
		sendChatMessage,
		sendChatFile,
		subscribeToUserTimeline,
		presence
	};
};

export const uploadBase64File = (
	apiUrl: string, base64file: any, captcha?: string, uploadProgress?: (progress: number) => void, secured = true
): Promise<FileUploadResponse> => {
	const onUploadProgress = ({loaded, total}: {loaded: number, total: number}) => {
		const percent = Math.ceil((loaded / total) * 100);
		if (uploadProgress) {
			uploadProgress(percent);
		}
	};
	return new Promise((res, rej) =>
		axios.post(apiUrl + '/files/base64', { base64file, captcha, useInvisible: true, secured }, {onUploadProgress})
			.then((response: any) => {
				res(response.data);
			})
			.catch((e: Error) => rej(e))
	);
};

export const submitTicket = (apiUrl: string, data: any) => {
	return new Promise((res, rej) =>
		axios.post(apiUrl + '/internal/tickets/guest', data)
			.then((ticketSuccess) => res(ticketSuccess))
			.catch((e) => rej(e))
	);
};

export type WidgetKbApi = {
	searchArticlesNewApi: (filters: any) => Promise<Article[]>,
	textualSearch: (text: string) => Promise<Article[]>,
	suggestionsSearch: (text: string) => Promise<Article[]>,
	getArticlesByCategoryId: (categoryId: string) => Promise<Article[]>,
	getArticleNewApi: (articleId: string) => Promise<Article>,
	getArticleByUriNewApi: (uri: string) => Promise<Article>,
	getRelatedArticlesNewApi: (articleId: string) => Promise<Article[]>,
	rateArticle: (
		articleId: string,
		newRating: boolean | null,
		oldRating: boolean | null) => Promise<{}>,
	voteArticle: (articleId: string) => Promise<{}>,
	removeVoteArticle: (articleId: string) => Promise<{}>
};

export const createWidgetKBApi = (
	httpService: HttpService, apiUrl: string, locale: string, widgetId: string): WidgetKbApi => {

	const searchArticlesNewApi = (filters: any): Promise<Article[]> => {
		return new Promise((res, rej) =>
			httpService.post<SearchResult<Article>>(apiUrl + '/widgets/' + widgetId + '/articles/search', filters)
				.then((response) => {
					res(response.items);
				})
				.catch((e) => rej(e))
		);
	};

	const textualSearch = (text: string) => {
		const filter = {
			text,
			locale,
			sourceType: SourceType.WIDGET,
			sourceId: widgetId
		};
		return searchArticlesNewApi(filter);
	};

	const suggestionsSearch = (text: string) => {
		const filter = {
			text,
			locale,
			sourceType: SourceType.WIDGET,
			sourceId: widgetId,
			pageSize: 3
		};
		return searchArticlesNewApi(filter);
	};

	const getArticlesByCategoryId = (categoryId: string) => {
		const filter = {
			page: 1,
			pageSize: 100,
			sortType: ArticleSortType.POSITION,
			locale,
			sourceType: SourceType.WIDGET,
			sourceId: widgetId,
			categoryIds: [categoryId]
		};
		return searchArticlesNewApi(filter);
	};

	const getArticleNewApi = (articleId: string) => {
		const defaultLocaleFallback = true;
		const url = `${apiUrl}/widgets/${widgetId}/articles/${articleId}?`
			+ queryString.stringify({ locale, defaultLocaleFallback });
		return httpService.get<Article>(url);
	};

	const getArticleByUriNewApi = (uri: string) => {
		const url = `${apiUrl}/widgets/${widgetId}/articles/uri/${uri}?` + queryString.stringify({ locale });
		return httpService.get<Article>(url);
	};

	const getRelatedArticlesNewApi = (articleId: string) => {
		const url = `${apiUrl}/widgets/${widgetId}/articles/${articleId}/relations?` + queryString.stringify({ locale });
		return httpService.get<Article[]>(url);
	};

	const rateArticle = (articleId: string, newRating: boolean | null, oldRating: boolean | null): Promise<any> => {
		const params = {
			newState: getHelpfulStateFromRating(newRating),
			state: getHelpfulStateFromRating(oldRating),
			locale,
			sourceType: SourceType.WIDGET,
			sourceId: widgetId
		};

		return httpService.post(`${apiUrl}/articles/${articleId}/helpful`, params);
	};

	const voteArticle = (articleId: string): Promise<any> => {
		const uri = apiUrl + '/articles/' + articleId + '/vote';
		return httpService.post(uri, { locale, sourceType: SourceType.WIDGET, sourceId: widgetId });
	};

	const removeVoteArticle = (articleId: string): Promise<any> => {
		const uri = `${apiUrl}/articles/${articleId}/vote`;
		const queryParams = `?locale=${locale}&sourceType=${SourceType.WIDGET}&sourceId=${widgetId}`;
		return httpService.delete(`${uri}${queryParams}`);
	};

	const getHelpfulStateFromRating = (rating: boolean | null) => {
		let helpfulState: HelpfulState;
		switch (rating) {
			case true:
				helpfulState = HelpfulState.HELPFUL;
				break;
			case false:
				helpfulState = HelpfulState.NOT_HELPFUL;
				break;
			default:
				helpfulState = HelpfulState.NONE;
		}
		return helpfulState;
	};

	return {
		searchArticlesNewApi,
		textualSearch,
		suggestionsSearch,
		getArticlesByCategoryId,
		getArticleNewApi,
		getArticleByUriNewApi,
		getRelatedArticlesNewApi,
		rateArticle,
		voteArticle,
		removeVoteArticle
	};
};
